import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown } = FiIcons;

const allFaqs = [
  { question: "How does RenewRX work?", answer: "It's simple! First, you complete a quick online intake form detailing your health history and weight loss goals. Next, a licensed medical professional reviews your file to determine if treatment is right for you. If approved, your prescription is sent to our partner pharmacy and shipped directly to your door." },
  { question: "Do I need insurance?", answer: "No, you do not need insurance. RenewRX operates on a transparent, cash-pay model. The price you see is the price you pay, which includes the medical consultation, prescription, and overnight shipping of your medication." },
  { question: "Are the medications safe?", answer: "Yes, all medications are prescribed by licensed healthcare professionals based on your specific medical profile. Our partner pharmacies are fully licensed and FDA-registered 503B compounding facilities or standard retail pharmacies, ensuring the highest standards of safety and quality." },
  { question: "Who is eligible for weight loss medications?", answer: "Eligibility is determined by a licensed medical provider. Generally, GLP-1 weight loss medications are prescribed for adults with a BMI of 30 or higher, or a BMI of 27 or higher with at least one weight-related medical condition (such as high blood pressure, type 2 diabetes, or high cholesterol)." },
  { question: "How quickly will I receive my medication?", answer: "Once a provider reviews and approves your treatment plan, the prescription is sent to the pharmacy. Processing typically takes 1-2 business days, and the medication is shipped directly to your home via expedited delivery." },
  { question: "What are the common side effects?", answer: "Common side effects for GLP-1 medications can include nausea, stomach upset, diarrhea, vomiting, and constipation. These typically occur when first starting the medication or increasing the dose. Your provider will guide you on how to manage and minimize these side effects." }
];

export function HomeFaq() {
  const [openIndex, setOpenIndex] = useState(0);
  const homeFaqs = allFaqs.slice(0, 4); // Only show first 4 on home page

  return (
    <section className="py-24 bg-[#fafaf9] relative overflow-hidden border-t border-slate-200/50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-sm font-bold text-teal-700 tracking-widest uppercase mb-4">Got Questions?</h2>
          <h3 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">Frequently Asked Questions</h3>
          <p className="text-xl text-slate-600 leading-relaxed font-light max-w-2xl mx-auto">
            Find answers to common questions about our treatments, process, and policies.
          </p>
        </div>
        <div className="space-y-4">
          {homeFaqs.map((faq, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1, duration: 0.5 }} className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden premium-shadow">
              <button className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none hover:bg-slate-50 transition-colors" onClick={() => setOpenIndex(openIndex === index ? null : index)}>
                <span className="text-lg font-bold text-slate-900">{faq.question}</span>
                <SafeIcon icon={FiChevronDown} className={`w-5 h-5 text-teal-600 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                    <div className="px-6 pb-6 text-slate-600 leading-relaxed border-t border-slate-50 pt-4 font-light">{faq.answer}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-32 pb-20 bg-slate-50 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-sm font-bold text-teal-600 tracking-widest uppercase mb-4">Support</h1>
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">Frequently Asked Questions</h2>
          <p className="text-xl text-slate-600 leading-relaxed">Find answers to common questions about our treatments, process, and policies.</p>
        </div>
        <div className="space-y-4">
          {allFaqs.map((faq, index) => (
            <div key={index} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <button className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" onClick={() => setOpenIndex(openIndex === index ? null : index)}>
                <span className="text-lg font-bold text-slate-900">{faq.question}</span>
                <SafeIcon icon={FiChevronDown} className={`w-5 h-5 text-teal-600 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                    <div className="px-6 pb-6 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{faq.answer}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}